import React, { useEffect, useMemo, useState } from "react";
import SecurityIcon from "@mui/icons-material/Security";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import SearchIcon from "@mui/icons-material/Search";
import GroupIcon from "@mui/icons-material/Group";
import PersonIcon from "@mui/icons-material/Person";
import axios from "axios";
import { NGROK } from "../../../../APIs";
import TailwindSwitch from "../../../../tailwind-ui/components/TailwindSwitch";
import { awsIcons } from "../AWSIcons";
import { parseAwsPolicyDocument, getAwsPolicyStatements } from "../utils/parseAwsPolicyDocument";

const BLUE = "#3b83f6";
const RESOURCE_ROLE_FILTERS = new Set([
  "virtual_machine",
  "storage",
  "database",
  "kubernetes",
]);
const EMPTY_RESOURCE_ROLES = {
  virtual_machine: [],
  storage: [],
  database: [],
  kubernetes: [],
};
const RESOURCE_NAME_MATCHERS = {
  virtual_machine: (name) =>
    name.includes("ec2") || name.includes("elastic compute cloud"),
  storage: (name) => name.includes("s3") || name.includes("simple storage"),
  database: (name) =>
    name.includes("rds") || name.includes("relational database"),
  kubernetes: (name) =>
    name.includes("eks") ||
    name.includes("kubernetes") ||
    name.includes("cluster"),
};

const sortResourceItems = (items, filter, nameSelector) =>
  [...items].sort((first, second) => {
    const firstName = nameSelector(first) || "";
    const secondName = nameSelector(second) || "";
    const matcher = RESOURCE_NAME_MATCHERS[filter];
    const priorityDifference =
      Number(!matcher(firstName.toLowerCase())) -
      Number(!matcher(secondName.toLowerCase()));
    return (
      priorityDifference ||
      firstName.localeCompare(secondName, undefined, { sensitivity: "base" })
    );
  });

const normalizePolicyArn = (value) =>
  String(value || "").trim().toLowerCase();

const toSearchableText = (value) => {
  if (Array.isArray(value)) return value.join(" ");
  if (value && typeof value === "object") return JSON.stringify(value);
  return value == null ? "" : String(value);
};

const statementMatchesSearch = (statement, query) => {
  if (!query) return true;

  const searchable = [
    statement?.Sid,
    statement?.Effect,
    statement?.Action,
    statement?.NotAction,
    statement?.Resource,
    statement?.NotResource,
    statement?.Condition,
    statement?.Principal,
    statement?.NotPrincipal,
  ]
    .map(toSearchableText)
    .join(" ")
    .toLowerCase();

  return searchable.includes(query);
};

const Sk = ({ className = "" }) => (
  <div className={`animate-pulse rounded-md bg-gray-200 dark:bg-gray-700/60 ${className}`} />
);

const RolesListSkeleton = () => (
  <div className="flex min-h-0 flex-1 flex-col w-full gap-4">
    <div className="role-dashboard-grid">
      <div className="role-panel">
        <div className="role-panel-header">
          <div>
            <Sk className="h-4 w-24 rounded" />
            <Sk className="h-3 w-52 rounded mt-2" />
          </div>
          <Sk className="h-6 w-9 rounded-full" />
        </div>

        <div className="role-search-shell">
          <Sk className="h-10 w-full rounded-xl" />
        </div>

        <div className="role-list">
          {Array.from({ length: 10 }).map((_, idx) => (
            <div
              key={idx}
              className="role-row"
            >
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <Sk className="h-10 w-10 rounded-xl flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <Sk className="h-4 w-2/3 rounded" />
                  <Sk className="h-3 w-1/3 rounded mt-2" />
                </div>
              </div>
              <Sk className="h-6 w-12 rounded-full flex-shrink-0" />
            </div>
          ))}
        </div>
      </div>

      <div className="role-panel">
        <div className="role-detail-header">
          <Sk className="h-10 w-10 rounded-xl flex-shrink-0" />
          <div className="min-w-0 flex-1">
            <Sk className="h-5 w-2/3 rounded" />
            <Sk className="h-4 w-1/3 rounded mt-2" />
          </div>
        </div>

        <div className="role-detail-tabs">
          <Sk className="h-8 w-20 rounded-lg" />
          <Sk className="h-8 w-28 rounded-lg" />
        </div>

        <div className="role-detail-content role-info-stack">
          {Array.from({ length: 4 }).map((_, idx) => (
            <div key={idx} className="role-info-card">
              <Sk className="h-3 w-24 rounded" />
              <Sk className="h-4 w-full rounded mt-2" />
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const AWSGenericRoles = ({ latestTenant, selectedAwsAccount, roleFilter = "all" }) => {
  const [filter, setFilter] = useState(roleFilter);
  const [roles, setRoles] = useState([]);
  const [resourceRoles, setResourceRoles] = useState(EMPTY_RESOURCE_ROLES);
  const [loading, setLoading] = useState(true);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedPermission, setSelectedPermission] = useState(null);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [policySearch, setPolicySearch] = useState("");
  const [permissionDocumentSearch, setPermissionDocumentSearch] = useState("");
  const [filteredPermission, setFilteredPermission] = useState([]);
  const [filteredusers, setFilteredUsers] = useState([]);
  const [filteredgroups, setFilteredGroups] = useState([]);
  const [modalPermission, setModalPermission] = useState(null);
  const [selectedDetailsTab, setSelectedDetailsTab] = useState(0);
  const [policyDetailsTab, setPolicyDetailsTab] = useState("permissions");
  const [isPermissionLoading, setIsPermissionLoading] = useState(false);
  const [publishError, setPublishError] = useState("");

  const getAllUsers = async () => {
    const response = await axios.get(
      `${NGROK}/api/aws/getIamusers?tenantName=${latestTenant}&accountId=${selectedAwsAccount}`
    );
    if (response.status === 200) return response.data;
    else return [];
  };

  const getAllRoles = async () => {
    const response = await axios.get(
      `${NGROK}/api/aws/getIamroles?tenantName=${latestTenant}&accountId=${selectedAwsAccount}`
    );
    if (response.status === 200) return response.data;
    else return [];
  };

  const getAwsResourcesRoles = async () => {
    const response = await axios.get(
      `${NGROK}/api/aws/getAwsResourcesRoles`,
      {
        params: {
          tenantName: latestTenant,
          accountId: selectedAwsAccount,
        },
      }
    );
    return Object.fromEntries(
      Object.keys(EMPTY_RESOURCE_ROLES).map((resourceFilter) => [
        resourceFilter,
        sortResourceItems(
          response.data?.[resourceFilter] || [],
          resourceFilter,
          (policy) => policy?.policyName
        ),
      ])
    );
  };

  const handleFilterChange = (newFilter) => {
    const filteredRoles = filterRoles(roles, newFilter);
    setFilter(newFilter);
    setSelectedRole(filteredRoles.length > 0 ? filteredRoles[0] : null);
    setSelectedPermission(null);
    setModalPermission(null);
    setPermissionDocumentSearch("");
    setSelectedDetailsTab(0);
  };

  const filterRoles = (roles, filter, resources = resourceRoles) => {
    if (!roles) return [];
    if (RESOURCE_ROLE_FILTERS.has(filter)) {
      const policyArns = new Set(
        (resources[filter] || []).map((policy) =>
          normalizePolicyArn(policy.policyARN)
        )
      );
      return sortResourceItems(
        roles.filter(
          (role) =>
            !role.roleName?.startsWith("Whiteswan-Role") &&
            role.rolePolicies?.some((policy) =>
              policyArns.has(normalizePolicyArn(policy.policyARN))
            )
        ),
        filter,
        (role) => role?.roleName
      );
    }
    return roles.filter((role) => {
      const { roleName } = role;
      if (roleName?.startsWith("Whiteswan-Role")) return false;
      if (filter === "default") {
        return roleName?.startsWith("AWSServiceRoleFor");
      } else if (filter === "custom") {
        return !roleName?.startsWith("AWSServiceRoleFor");
      }
      return true;
    });
  };

  const filteredRoles = filterRoles(
    roles?.filter(
      (role) =>
        role?.roleName &&
        role?.roleName.toLowerCase().includes(search.toLowerCase())
    ),
    filter
  );

  const fetchData = async (initialLoad = false) => {
    if (!latestTenant || !selectedAwsAccount) {
      setUsers([]);
      setRoles([]);
      setResourceRoles(EMPTY_RESOURCE_ROLES);
      setSelectedRole(null);
      setSelectedPermission(null);
      setFilteredPermission([]);
      setLoading(false);
      return;
    }
    try {
      const [usersData, rolesData, resourceRolesData] = await Promise.all([
        getAllUsers(),
        getAllRoles(),
        getAwsResourcesRoles().catch(() => EMPTY_RESOURCE_ROLES),
      ]);
      setUsers(usersData);
      setRoles(rolesData);
      setResourceRoles(resourceRolesData);
      setLoading(false);
      const visibleRoles = filterRoles(rolesData, filter, resourceRolesData);
      setSelectedRole((currentRole) => {
        if (!initialLoad && currentRole) {
          const refreshedRole = visibleRoles.find(
            (role) => role.arn === currentRole.arn
          );
          if (refreshedRole) return refreshedRole;
        }
        return visibleRoles.length > 0 ? visibleRoles[0] : null;
      });
    } catch (error) {
      console.error("Error fetching data", error);
      setUsers([]);
      setRoles([]);
      setResourceRoles(EMPTY_RESOURCE_ROLES);
      setSelectedRole(null);
      setLoading(false);
    }
  };

  const handleRoleSearchChange = (event) => {
    setSearch(event.target.value);
  };

  const handleSearchSubmit = () => {
    // Search is already reactive via state
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      handleSearchSubmit();
    }
  };

  const handleRoleClick = (role) => {
    setSelectedRole(role);
    setSelectedPermission(null);
    setModalPermission(null);
    setPermissionDocumentSearch("");
    setSelectedDetailsTab(0);
  };

  const consolidatedPermissions = useMemo(
    () =>
      selectedRole
        ? RESOURCE_ROLE_FILTERS.has(filter)
          ? selectedRole.rolePolicies.filter((policy) =>
              (resourceRoles[filter] || []).some(
                (resourcePolicy) =>
                  normalizePolicyArn(resourcePolicy.policyARN) ===
                  normalizePolicyArn(policy.policyARN)
              )
            )
          : [...selectedRole.rolePolicies]
        : [],
    [selectedRole, filter, resourceRoles]
  );

  const handlePolicySearchChange = (event) => {
    setPolicySearch(event.target.value);
  };

  const handlePermissionClick = async (permission) => {
    setSelectedPermission(permission.policyARN);
    setIsPermissionLoading(true);
    setPolicyDetailsTab("permissions");
    setPermissionDocumentSearch("");

    const policyArn = permission.policyARN;
    const groupsWithPolicy = users
      .flatMap((user) => user.groups || [])
      .filter((group) =>
        group.groupPolicies?.some((policy) => policy.policyARN === policyArn)
      );
    const usersWithPolicy = users.filter((user) => {
      const hasPolicyInGroups = user.groups?.some((group) =>
        group.groupPolicies?.some((policy) => policy.policyARN === policyArn)
      );
      const hasPolicyInUserPolicies = user.userPolicies?.some(
        (policy) => policy.policyARN === policyArn
      );
      return hasPolicyInGroups || hasPolicyInUserPolicies;
    });

    setFilteredUsers(usersWithPolicy);
    setFilteredGroups(groupsWithPolicy);

    const permissionDoc = await getTheSpecificPolicyDocument(permission.policyARN);
    if (permissionDoc) {
      permissionDoc.policyDocument = parseAwsPolicyDocument(permissionDoc.policyDocument);
      setModalPermission(permissionDoc);
    } else {
      setModalPermission(null);
    }
    setIsPermissionLoading(false);
  };

  const getTheSpecificPolicyDocument = async (policyArn) => {
    try {
      const response = await axios.get(
        `${NGROK}/api/aws/getSpecificIAMPolicydetails?tenantName=${latestTenant}&policyarn=${encodeURIComponent(policyArn)}&accountId=${selectedAwsAccount}`,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        }
      );
      return response.data;
    } catch (error) {
      return null;
    }
  };

  const updatePermissionARN = async (arn) => {
    setPublishError("");
    const previousPermissions = filteredPermission;
    const updatedPermissions = filteredPermission.map((permission) => {
      if (permission.policyARN === arn) {
        return {
          ...permission,
          isManagedAttachedPolicies: !permission.isManagedAttachedPolicies,
        };
      }
      return permission;
    });

    setFilteredPermission(updatedPermissions);

    try {
      const response = await axios.put(
        `${NGROK}/api/aws/publishPolicies?tenantName=${latestTenant}&policyARN=${encodeURIComponent(arn)}&accountId=${selectedAwsAccount}`
      );
      if (response?.data !== true) {
        throw new Error("Publish policy request did not succeed");
      }
      await fetchData();
    } catch (err) {
      console.error(err);
      setPublishError("Failed to publish policy. Please try again.");
      setFilteredPermission(previousPermissions);
    }
  };

  useEffect(() => {
    fetchData(true);
  }, [latestTenant, selectedAwsAccount]);

  useEffect(() => {
    setFilteredPermission(
      policySearch.length === 0
        ? consolidatedPermissions
        : consolidatedPermissions.filter(
            (permission) =>
              permission?.policyName &&
              permission.policyName
                .toLowerCase()
                .includes(policySearch.toLowerCase())
          )
    );
  }, [policySearch, consolidatedPermissions]);

  useEffect(() => {
    setLoading(Boolean(selectedAwsAccount));
  }, [selectedAwsAccount]);

  useEffect(() => {
    handleFilterChange(roleFilter);
  }, [roleFilter]);

  const isDefaultRole = (roleName) => roleName?.startsWith("AWSServiceRoleFor");
  const selectedKey = selectedRole?.arn;

  const renderPolicyDetails = () => {
    if (isPermissionLoading) {
      return (
        <div className="flex justify-center py-8">
          <div
            className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin"
            style={{ borderColor: BLUE, borderTopColor: "transparent" }}
          />
        </div>
      );
    }

    if (!selectedPermission || !modalPermission) {
      return (
        <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
          <VpnKeyIcon sx={{ fontSize: 36, color: "rgba(0,0,0,0.25)" }} />
          <p className="text-sm mt-2">Select a policy to view details</p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {/* Policy Info */}
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">
              ARN
            </label>
            <p className="text-sm text-foreground/85 font-mono break-all">{modalPermission?.policyARN || "—"}</p>
          </div>
        </div>

        {/* Sub-tabs for Users/Groups/Permissions */}
        <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
          <div className="flex gap-4 border-b border-gray-200 dark:border-gray-700 mb-4">
            {[
              { key: "permissions", label: "Permissions", icon: VpnKeyIcon },
              { key: "users", label: `Users (${filteredusers?.length || 0})`, icon: PersonIcon },
              { key: "groups", label: `Groups (${filteredgroups?.length || 0})`, icon: GroupIcon },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setPolicyDetailsTab(tab.key)}
                className={`flex items-center gap-1.5 pb-2 text-xs font-medium border-b-2 transition-all ${policyDetailsTab === tab.key
                  ? "border-b-2"
                  : "text-muted-foreground border-transparent hover:text-foreground/80"
                  }`}
                style={policyDetailsTab === tab.key ? { color: BLUE, borderColor: BLUE } : undefined}
              >
                <tab.icon sx={{ fontSize: 14 }} />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div className="space-y-3">
            {policyDetailsTab === "users" && (
              filteredusers?.length > 0 ? (
                <div className="space-y-2">
                  {filteredusers.map((user, index) => (
                    <div key={index} className="px-3 py-2 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                      <p className="text-sm font-medium text-foreground/90">{user.username}</p>
                      <p className="text-xs text-muted-foreground break-all">{user.arn}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">No users assigned</p>
              )
            )}

            {policyDetailsTab === "groups" && (
              filteredgroups?.length > 0 ? (
                <div className="space-y-2">
                  {filteredgroups.map((group, index) => (
                    <div key={index} className="px-3 py-2 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                      <p className="text-sm font-medium text-foreground/90">{group.groupName}</p>
                      <p className="text-xs text-muted-foreground break-all">{group.arn}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">No groups assigned</p>
              )
            )}

            {policyDetailsTab === "permissions" && (
              (() => {
                const statements = getAwsPolicyStatements(modalPermission?.policyDocument);
                const normalizedSearch = permissionDocumentSearch.trim().toLowerCase();
                const filteredStatements = statements
                  .map((stmt, stmtIndex) => ({ stmt, stmtIndex }))
                  .filter(({ stmt }) => statementMatchesSearch(stmt, normalizedSearch));
                return statements.length > 0 ? (
                <div className="space-y-3 text-sm">
                  <div>
                    <span className="text-xs font-semibold text-muted-foreground uppercase">Version:</span>
                    <span className="ml-2 text-foreground/85">
                      {parseAwsPolicyDocument(modalPermission.policyDocument)?.Version || "—"}
                    </span>
                  </div>

                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search permissions..."
                      value={permissionDocumentSearch}
                      onChange={(event) => setPermissionDocumentSearch(event.target.value)}
                      className="role-search-input"
                    />
                    <SearchIcon
                      fontSize="small"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                    />
                  </div>

                  {filteredStatements.length > 0 ? filteredStatements.map(({ stmt, stmtIndex }) => (
                    <div key={stmtIndex} className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-xl space-y-2">
                      <div className="text-xs font-semibold text-foreground/90">
                        Statement {stmtIndex + 1}
                      </div>
                      {stmt.Sid && (
                        <div>
                          <span className="text-xs text-muted-foreground">Sid:</span>
                          <span className="ml-2 text-foreground/85">{stmt.Sid}</span>
                        </div>
                      )}
                      <div>
                        <span className="text-xs text-muted-foreground">Effect:</span>
                        <span className="ml-2 text-foreground/85">{stmt.Effect}</span>
                      </div>
                      <div>
                        <span className="text-xs text-muted-foreground block mb-1">Action:</span>
                        <ul className="list-disc list-inside text-foreground/85">
                          {Array.isArray(stmt.Action) ? (
                            stmt.Action.map((a, i) => <li key={i} className="break-all">{a}</li>)
                          ) : (
                            <li className="break-all">{stmt.Action}</li>
                          )}
                        </ul>
                      </div>
                      <div>
                        <span className="text-xs text-muted-foreground block mb-1">Resource:</span>
                        <ul className="list-disc list-inside text-foreground/85">
                          {Array.isArray(stmt.Resource) ? (
                            stmt.Resource.map((r, i) => <li key={i} className="break-all">{r}</li>)
                          ) : (
                            <li className="break-all">{stmt.Resource}</li>
                          )}
                        </ul>
                      </div>
                    </div>
                  )) : (
                    <p className="text-sm text-muted-foreground text-center py-4">No permissions match your search</p>
                  )}
                </div>
                ) : (
                <p className="text-sm text-muted-foreground text-center py-4">No permissions data</p>
                );
              })()
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {loading ? (
        <RolesListSkeleton />
      ) : (
        <div className="flex min-h-0 flex-1 flex-col w-full gap-4">
          <div className="role-dashboard-grid">
            {/* Left Column - Roles List */}
            <div className="role-panel">
              <div className="role-panel-header">
                <div>
                  <div className="role-panel-title">Roles</div>
                  <div className="role-panel-subtitle">Select a role to inspect access and policies</div>
                </div>
                <span className="role-count-pill">{filteredRoles?.length || 0}</span>
              </div>

              <div className="role-search-shell">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search roles..."
                    value={search}
                    onChange={handleRoleSearchChange}
                    onKeyDown={handleKeyDown}
                    className="role-search-input"
                  />
                  <button
                    onClick={handleSearchSubmit}
                    className="role-search-icon"
                    aria-label="Search"
                    type="button"
                  >
                    <SearchIcon fontSize="small" />
                  </button>
                </div>
              </div>

              <div className="role-list">
                {filteredRoles?.length > 0 ? (
                  filteredRoles
                    .filter((role) => !role.roleName.startsWith("Whiteswan"))
                    .map((role, index) => {
                      const roleKey = role.arn || index;
                      const isSelected = roleKey === selectedKey;

                      return (
                        <div
                          key={roleKey}
                          onClick={() => handleRoleClick(role)}
                          className={`role-row ${isSelected ? "active" : ""}`}
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="role-avatar">
                              {awsIcons.roles}
                            </div>
                            <div className="role-row-text">
                              <p className="role-row-title">
                                {role.roleName}
                              </p>
                              <p className="role-row-subtitle">
                                {isDefaultRole(role.roleName) ? "AWS Managed Role" : "Custom Role"}
                              </p>
                            </div>
                          </div>
                          <span className="role-type-badge">
                            {isDefaultRole(role.roleName) ? "Default" : "Custom"}
                          </span>
                        </div>
                      );
                    })
                ) : (
                  <div className="role-empty-state">
                    <SecurityIcon sx={{ fontSize: 44, color: "rgba(0,0,0,0.25)" }} />
                    <p className="text-sm mt-2">No roles found</p>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - Details Panel */}
            <div className="role-panel">
              {selectedRole ? (
                <>
                  {/* Role Header */}
                  <div className="role-detail-header">
                    <div className="role-avatar">{awsIcons.roles}</div>
                    <div className="min-w-0">
                      <h3 className="role-detail-title">{selectedRole?.roleName}</h3>
                      <p className="role-detail-subtitle">
                        {isDefaultRole(selectedRole?.roleName) ? "AWS Managed Role" : "Custom Role"}
                      </p>
                    </div>
                  </div>

                  {/* Tabs */}
                  <div className="role-detail-tabs">
                    <button
                      onClick={() => setSelectedDetailsTab(0)}
                      className={`role-detail-tab ${selectedDetailsTab === 0 ? "active" : ""}`}
                    >
                      <InfoOutlinedIcon fontSize="small" />
                      Info
                    </button>
                    <button
                      onClick={() => setSelectedDetailsTab(1)}
                      className={`role-detail-tab ${selectedDetailsTab === 1 ? "active" : ""}`}
                    >
                      <VpnKeyIcon fontSize="small" />
                      Policies
                    </button>
                  </div>

                  {/* Tab Content */}
                  <div className="role-detail-content">
                    {selectedDetailsTab === 0 && (
                      <div className="role-info-stack">
                        <div className="role-info-card">
                          <div className="role-info-label">Role Name</div>
                          <div className="role-info-value">
                            {selectedRole?.roleName || "—"}
                          </div>
                        </div>

                        <div className="role-info-card">
                          <div className="role-info-label">Role ARN</div>
                          <div className="role-info-value font-mono">
                            {selectedRole?.arn || "—"}
                          </div>
                        </div>

                        <div className="role-info-card">
                          <div className="role-info-label">Attached Policies</div>
                          <div className="role-info-value">
                            {selectedRole?.rolePolicies?.length || 0} policies
                          </div>
                        </div>
                      </div>
                    )}

                    {selectedDetailsTab === 1 && (
                      <div className="space-y-4">
                        {publishError && (
                          <p className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg px-3 py-2">
                            {publishError}
                          </p>
                        )}
                        {/* Policy Search */}
                        <div className="relative">
                          <input
                            type="text"
                            placeholder="Search policies..."
                            value={policySearch}
                            onChange={handlePolicySearchChange}
                            className="w-full pl-4 pr-10 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-foreground/85 placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:border-transparent transition-all"
                          />
                          <SearchIcon
                            fontSize="small"
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                          />
                        </div>

                        {/* Policies List */}
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                            Attached Policies
                          </span>
                          <span
                            className="px-2 py-0.5 text-xs font-semibold rounded-full"
                            style={{ backgroundColor: "rgba(59,131,246,0.12)", color: BLUE }}
                          >
                            {filteredPermission?.length || 0}
                          </span>
                        </div>

                        <div className="space-y-2 max-h-40 overflow-y-auto">
                          {filteredPermission?.length > 0 ? (
                            filteredPermission.map((permission, idx) => (
                              <div
                                key={idx}
                                onClick={() => handlePermissionClick(permission)}
                                className={`flex items-center justify-between px-3 py-2 rounded-xl cursor-pointer transition-all ${selectedPermission === permission.policyARN
                                  ? "bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800"
                                  : "bg-gray-50 dark:bg-gray-800/50 border border-transparent portal-card-hover"
                                  }`}
                              >
                                <div className="flex items-center gap-2 min-w-0 flex-1">
                                  <div
                                    className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                                    style={{ backgroundColor: BLUE }}
                                  />
                                  <span className="text-sm text-foreground/85 truncate">
                                    {permission?.policyName}
                                  </span>
                                </div>
                                <div onClick={(e) => e.stopPropagation()} className="flex items-center gap-2 flex-shrink-0">
                                  <span className="text-xs text-muted-foreground">Publish</span>
                                  <TailwindSwitch
                                    isDisabled={false}
                                    data={permission}
                                    isChecked={Boolean(permission.isManagedAttachedPolicies)}
                                    action={(e, data) => updatePermissionARN(data?.policyARN)}
                                  />
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                              <VpnKeyIcon sx={{ fontSize: 36, color: "rgba(0,0,0,0.25)" }} />
                              <p className="text-sm mt-2">No policies attached</p>
                            </div>
                          )}
                        </div>

                        {/* Policy Details Section */}
                        <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                          {renderPolicyDetails()}
                        </div>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="role-empty-state">
                  <SecurityIcon sx={{ fontSize: 52, color: "rgba(0,0,0,0.25)" }} />
                  <p className="text-sm mt-2">Select a role to view details</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AWSGenericRoles;
